import { useMemo } from 'react';
import { Box, BoxProps, Link } from '@mui/material';
import { useAuth } from 'providers/AuthProvider';
import { useThemeMode } from 'hooks/useThemeMode';
import { weddingPaths } from 'routes/paths';

// Public folder assets — served as static files by Vite
const NO_SYMBOL_LOGO = '/logo/no-symbol-logo/svg/Color logo - no background.svg';
const NO_SYMBOL_WHITE_LOGO = '/logo/no-symbol-logo/svg/White logo - no background.svg';
const SYMBOL_LOGO = '/logo/symbol-logo/agapae-symbol-only.svg';
const SYMBOL_WHITE_LOGO = '/logo/symbol-logo/agapae-symbol-only-white.svg';

interface LogoProps {
  showName?: boolean;
  variant?: 'auto' | 'white' | 'color';
  sx?: BoxProps['sx'];
  href?: string;
}

const Logo = ({ showName = true, variant = 'auto', sx, href: hrefOverride }: LogoProps) => {
  const { sessionUser } = useAuth();
  const { isDark } = useThemeMode();

  const isAuthenticated = !!sessionUser;

  let src: string;
  let defaultHeight: number;

  if (variant === 'color') {
    // Full-color logo — used on light backgrounds (e.g. the join page's neutral
    // loading backdrop, where the white logo would be invisible).
    src = NO_SYMBOL_LOGO;
    defaultHeight = 78;
  } else if (variant === 'white' || !isAuthenticated) {
    // White logo — used on auth layout pages (dark image background)
    src = NO_SYMBOL_WHITE_LOGO;
    defaultHeight = 78;
  } else if (showName) {
    // Expanded — wordmark, swapped to white in dark mode for contrast
    src = isDark ? NO_SYMBOL_WHITE_LOGO : NO_SYMBOL_LOGO;
    defaultHeight = 58;
  } else {
    // Collapsed — symbol only, swapped to white in dark mode for contrast
    src = isDark ? SYMBOL_WHITE_LOGO : SYMBOL_LOGO;
    defaultHeight = 42;
  }

  const logoHref = useMemo(() => {
    if (hrefOverride !== undefined) return hrefOverride;
    if (!isAuthenticated || !sessionUser) return weddingPaths.coupleLogin;
    if (sessionUser.role === 'uploader' && sessionUser.eventId) {
      return weddingPaths.guestDashboard(sessionUser.eventId);
    }
    return weddingPaths.coupleDashboard;
  }, [hrefOverride, isAuthenticated, sessionUser]);

  return (
    <Link href={logoHref} underline="none" sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        component="img"
        src={src}
        alt="Agapaé"
        sx={{
          height: { xs: !isAuthenticated ? 58 : defaultHeight, md: defaultHeight },
          width: 'auto',
          display: 'block',
          ...sx,
        }}
      />
    </Link>
  );
};

export default Logo;
